import type { CmmdWorkItemType } from "@cmmd-forge/contracts";
import {
  BugIcon,
  CircleDotIcon,
  FileTextIcon,
  FlaskConicalIcon,
  LightbulbIcon,
  WrenchIcon,
  type LucideIcon,
} from "lucide-react";

import { cn } from "~/lib/utils";

export const WORK_ITEM_TYPE_OPTIONS: readonly CmmdWorkItemType[] = [
  "bug",
  "feature",
  "chore",
  "docs",
  "research",
  "other",
];

interface WorkItemTypeDescriptor {
  readonly Icon: LucideIcon;
  readonly colorClass: string;
  readonly label: string;
}

/*
 * Neutral, all six. This glyph sits next to the work-item id on every card, row,
 * and picker in the app, so a per-type hue meant six colors repeating on every
 * line of every surface — red bug, purple feature, orange chore, teal docs — none
 * of which is what the card is telling you. The orange one also collided with
 * `--primary` outright.
 *
 * Bug / lightbulb / wrench / document / flask / dot are six unmistakable
 * silhouettes already. Shape is what an icon is for; the color was carrying no
 * information the shape wasn't. The board's color budget is spent on the two
 * things that earn it: the workflow stage dots (the column axis) and genuine
 * exception states.
 */
const WORK_ITEM_TYPE_DESCRIPTORS: Record<CmmdWorkItemType, WorkItemTypeDescriptor> = {
  bug: { Icon: BugIcon, colorClass: "text-muted-foreground", label: "Bug" },
  feature: { Icon: LightbulbIcon, colorClass: "text-muted-foreground", label: "Feature" },
  chore: { Icon: WrenchIcon, colorClass: "text-muted-foreground", label: "Chore" },
  docs: { Icon: FileTextIcon, colorClass: "text-muted-foreground", label: "Documentation" },
  research: { Icon: FlaskConicalIcon, colorClass: "text-muted-foreground", label: "Research" },
  other: { Icon: CircleDotIcon, colorClass: "text-muted-foreground", label: "Task" },
};

export function taskWorkItemTypeLabel(workItemType: CmmdWorkItemType): string {
  return WORK_ITEM_TYPE_DESCRIPTORS[workItemType].label;
}

export interface TaskWorkItemTypeIconProps {
  readonly workItemType: CmmdWorkItemType;
  readonly className?: string;
}

/** Canonical Forge presentation for CMMD's generated work-item type union. */
export function TaskWorkItemTypeIcon({ workItemType, className }: TaskWorkItemTypeIconProps) {
  const { Icon, colorClass, label } = WORK_ITEM_TYPE_DESCRIPTORS[workItemType];
  const accessibleLabel = `Type: ${label}`;
  return (
    <span
      role="img"
      aria-label={accessibleLabel}
      title={label}
      className={cn("inline-flex shrink-0 items-center", colorClass, className)}
    >
      <Icon className="size-3.5" aria-hidden />
    </span>
  );
}
